import type { JSX } from "react";
import { useRef, useState } from "react";

import { Popover as BasePopover } from "@base-ui/react/popover";
import { useControlContext } from "@frontend/contexts";

import {
  ColorPickerContext,
  makeColorPickerContext,
} from "./ColorPicker.context";
import type { ColorPickerProps } from "./ColorPicker.types";
import { hexToHsv, hsvToHex, normalizeHex } from "./colorMath";
import { DEFAULT_COLOR } from "./constants";

export function ColorPicker({
  size,
  value,
  defaultValue = DEFAULT_COLOR,
  onValueChange,
  disabled,
  arrow,
  open,
  defaultOpen,
  onOpenChange,
  modal,
  children,
}: ColorPickerProps): JSX.Element {
  const inherited = useControlContext();
  const resolvedSize = size ?? inherited.size;

  const pointerOffsetRef = useRef(0);

  const isControlled = value !== undefined;
  const [internalValue, setInternalValue] = useState(
    () => normalizeHex(defaultValue) ?? DEFAULT_COLOR,
  );
  const currentHex =
    normalizeHex(isControlled ? value : internalValue) ?? DEFAULT_COLOR;
  const hsv = hexToHsv(currentHex) ?? { h: 0, s: 0, v: 0 };

  function commit(nextHex: string) {
    if (!isControlled) {
      setInternalValue(nextHex);
    }

    onValueChange?.(nextHex);
  }

  return (
    <ColorPickerContext.Provider
      value={makeColorPickerContext({
        size: resolvedSize,
        disabled,
        arrow,
        hex: currentHex,
        hsv,
        pointerOffsetRef,
        setHsv: (patch) => commit(hsvToHex({ ...hsv, ...patch })),
        setHex: (hex) => {
          const normalized = normalizeHex(hex);

          if (normalized) {
            commit(normalized);
          }
        },
      })}
    >
      <BasePopover.Root
        data-slot="color-picker-root"
        open={open}
        defaultOpen={defaultOpen}
        onOpenChange={onOpenChange}
        modal={modal}
      >
        {children}
      </BasePopover.Root>
    </ColorPickerContext.Provider>
  );
}
